import { useState, useEffect } from 'react';
import { enqueueSnackbar } from 'notistack';

import { LoadingButton } from '@mui/lab';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';

import { useTranslate } from 'src/locales';
import { CallList } from 'src/services/call-lists/call-lists.types';
import { useCreateCallListMutation } from 'src/services/call-lists/call-lists.service';

// ----------------------------------------------------------------------

type Props = {
  open: boolean;
  onClose: VoidFunction;
  onCreated?: (list: CallList) => void;
  /** Admins can make a list for a team member. */
  ownerId?: string | null;
};

/** A new call list with the default settings; they can be changed from the list's settings. */
export default function CallListNameModal({ open, onClose, onCreated, ownerId }: Props) {
  const { t } = useTranslate();
  const [name, setName] = useState('');
  const [createList, { isLoading }] = useCreateCallListMutation();

  useEffect(() => {
    if (open) setName('');
  }, [open]);

  const submit = async () => {
    if (!name.trim()) return;
    try {
      const list = await createList({ name: name.trim(), ...(ownerId ? { owner_id: ownerId } : {}) }).unwrap();
      enqueueSnackbar(t('call_lists.toasts.list_created'), { variant: 'success' });
      onCreated?.(list);
      onClose();
    } catch (error: any) {
      enqueueSnackbar(error?.data?.message || t('call_lists.toasts.action_failed'), { variant: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('call_lists.new_list.title')}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          margin="dense"
          label={t('call_lists.new_list.name')}
          placeholder={t('call_lists.new_list.placeholder')}
          value={name}
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') submit();
          }}
          inputProps={{ maxLength: 100 }}
        />
        <Typography variant="caption" color="text.secondary">
          {t('call_lists.new_list.caption')}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <LoadingButton variant="contained" loading={isLoading} disabled={!name.trim()} onClick={submit}>
          {t('call_lists.new_list.create')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
